import { useEffect, useMemo, useRef, useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { endpoints, type Channel } from "@/lib/api";
import { fmtNumber, platformLabel } from "@/lib/format";
import { cn } from "@/lib/utils";
import { Badge, lifecycleVariant } from "@/components/ui/Badge";

// ChannelPicker — typeahead-style channel selector. Replaces raw int input
// in places like 新建投放 and 记录交付视频. Preloads up to MAX_PRELOAD
// channels (paginated calls when above) so client-side fuzzy filtering
// stays snappy without burning the network.
//
// Usage:
//   <ChannelPicker value={form.channel_id} onChange={(id, ch) => ...} />
//
// Notes:
//   - Channels are scoped to current /api/channels filters (no platform
//     restriction by default; pass `platforms` if needed).
//   - The picker is uncontrolled wrt search text; only the resolved channel
//     id is reported via onChange.
const MAX_PRELOAD = 500;

interface ChannelPickerProps {
  value: number | null;
  onChange: (id: number, channel: Channel) => void;
  placeholder?: string;
  // When set, only channels in these lifecycle states show. Useful for
  // 新建投放 where you probably want candidate/active/evaluating only.
  lifecycles?: string[];
  // Optional preset filter for platform.
  platform?: string;
  disabled?: boolean;
}

export function ChannelPicker({
  value,
  onChange,
  placeholder = "搜频道名 / handle / ID…",
  lifecycles,
  platform,
  disabled,
}: ChannelPickerProps) {
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState("");
  const wrapRef = useRef<HTMLDivElement | null>(null);

  // Single pre-load. For a KOL library under ~500 channels, one fetch is
  // simpler than wiring server-side q= search.
  const channelsQ = useQuery({
    queryKey: ["channels-picker", platform ?? ""],
    queryFn: () => endpoints.channels({ platform, limit: MAX_PRELOAD }),
    staleTime: 5 * 60_000,
  });

  // Auto-close when clicking outside.
  useEffect(() => {
    if (!open) return;
    function onDoc(e: MouseEvent) {
      if (wrapRef.current && !wrapRef.current.contains(e.target as Node)) {
        setOpen(false);
      }
    }
    document.addEventListener("mousedown", onDoc);
    return () => document.removeEventListener("mousedown", onDoc);
  }, [open]);

  const all = channelsQ.data?.data ?? [];

  // Selected channel (for display in the closed-state button).
  const selected = useMemo(
    () => all.find((c) => c.ID === value),
    [all, value],
  );

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    let pool = all;
    if (lifecycles && lifecycles.length > 0) {
      pool = pool.filter((c) => lifecycles.includes(c.LifecycleStatus));
    }
    if (!q) return pool.slice(0, 50);
    return pool
      .filter((c) => {
        const hay = [
          c.Name ?? "",
          c.Handle ?? "",
          c.PlatformChannelID,
          String(c.ID),
          c.ContentType ?? "",
        ]
          .join(" ")
          .toLowerCase();
        return hay.includes(q);
      })
      .slice(0, 50);
  }, [all, query, lifecycles]);

  return (
    <div ref={wrapRef} className="relative">
      <button
        type="button"
        disabled={disabled}
        onClick={() => setOpen((o) => !o)}
        className={cn(
          "flex h-9 w-full items-center justify-between rounded-md border border-border bg-background px-3 text-left text-sm",
          "focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring",
          "disabled:cursor-not-allowed disabled:opacity-50",
        )}
      >
        {selected ? (
          <span className="truncate">
            <span className="text-muted-foreground">#{selected.ID}</span>{" "}
            <span className="font-medium">
              {selected.Name ?? selected.Handle ?? selected.PlatformChannelID}
            </span>
            {selected.Handle && selected.Name && (
              <span className="ml-1 text-xs text-muted-foreground">
                {selected.Handle}
              </span>
            )}
          </span>
        ) : (
          <span className="text-muted-foreground">
            {channelsQ.isLoading ? "加载中…" : placeholder}
          </span>
        )}
        <span className="ml-2 text-muted-foreground">▾</span>
      </button>

      {open && (
        <div className="absolute left-0 right-0 top-full z-20 mt-1 max-h-80 overflow-y-auto rounded-md border border-border bg-background shadow-lg">
          <input
            autoFocus
            type="text"
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            placeholder={placeholder}
            className="sticky top-0 z-10 w-full border-b border-border bg-background px-3 py-2 text-sm focus-visible:outline-none"
          />
          {filtered.length === 0 ? (
            <p className="p-3 text-xs text-muted-foreground">
              {query
                ? `没有匹配 "${query}" 的频道`
                : channelsQ.isLoading
                  ? "加载中…"
                  : "频道库是空的；先去 /channels 加几个"}
            </p>
          ) : (
            <ul>
              {filtered.map((c) => (
                <li key={c.ID}>
                  <button
                    type="button"
                    onClick={() => {
                      onChange(c.ID, c);
                      setOpen(false);
                      setQuery("");
                    }}
                    className={cn(
                      "block w-full px-3 py-2 text-left text-sm hover:bg-muted",
                      c.ID === value && "bg-muted",
                    )}
                  >
                    <div className="flex items-center gap-2">
                      <span className="text-xs text-muted-foreground">
                        #{c.ID}
                      </span>
                      <span className="font-medium">
                        {c.Name ?? c.Handle ?? c.PlatformChannelID}
                      </span>
                      {c.Handle && c.Name && (
                        <span className="text-xs text-muted-foreground">
                          {c.Handle}
                        </span>
                      )}
                    </div>
                    <div className="mt-0.5 flex flex-wrap items-center gap-2 text-xs text-muted-foreground">
                      <span>{platformLabel(c.Platform)}</span>
                      {c.FollowerCount !== null && (
                        <span>· {fmtNumber(c.FollowerCount)} 粉丝</span>
                      )}
                      <Badge variant={lifecycleVariant(c.LifecycleStatus)}>
                        {c.LifecycleStatus}
                      </Badge>
                      {c.ContentType && (
                        <span className="text-[10px]">· {c.ContentType}</span>
                      )}
                    </div>
                  </button>
                </li>
              ))}
            </ul>
          )}
        </div>
      )}
    </div>
  );
}
